import {
  mergeTyped,
  useDataStream,
  useStreamValue,
  useVoidEventAsStream
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { mapTo } from 'rxjs/operators';
import { updateCompany } from '../../../api/fetch-functions/company';
import { CompanyAPIRequestBodyType } from '../../../api/types/company/api-request-body';
import { maxStringLength } from '../../../constants/max-string-length';
import { viewTexts } from '../../../constants/view-texts';
import { useFetchResultState } from '../../../functions/hooks/use-fetch-result-state';
import {
  FormInputStateConfigType,
  useFormInputState
} from '../../../functions/hooks/use-form-input-state';
import { useToken } from '../../../global-state/auth-state';
import { fetchCompanyListOf } from '../../../global-state/database/company-list';
import { koumutenId$ } from '../../../global-state/router-id';
import { ICompanyType } from '../../../types/record/company';
import { EditCompanyDialogView } from './edit-company-dialog-view';

const formInputStateConfig: FormInputStateConfigType<CompanyAPIRequestBodyType> = {
  minStringLength: 1,
  maxStringLength: maxStringLength.companyName,
  stringLengthCheckMessageTargetName:
    viewTexts.stringLengthCheckMessageTargetName.companyName,
  required: true,
  validationErrorContentMapFn: content =>
    content.name !== undefined ? content.name.toArray() : []
};

interface Props {
  open: boolean;
  onClose: (reason: 'ok' | 'cancel' | '') => void;
  company: ICompanyType;
}

export const EditCompanyDialog: FC<Props> = memo(
  ({ open, onClose, company: companyInit }: Readonly<Props>) => {
    const token = useToken();

    /* state */

    const [
      // dummy comment
      fetchResult,
      setFetchResult
    ] = useFetchResultState<ICompanyType, CompanyAPIRequestBodyType>();

    const nonFieldErrors = useMemo<string[]>(
      () =>
        fetchResult !== undefined &&
        fetchResult.type === 'error' &&
        fetchResult.body.type === 'non-field-errors'
          ? fetchResult.body.content.toArray()
          : [],
      [fetchResult]
    );

    const [
      companyName$,
      companyNameErrors$,
      companyNameIsValid$,
      companyNameOnInput
    ] = useFormInputState<ICompanyType, CompanyAPIRequestBodyType>(
      open,
      fetchResult,
      formInputStateConfig,
      companyInit.name
    );

    const [submitOnClick$, submitOnClick] = useVoidEventAsStream();

    const submitButtonEnabled$ = useDataStream<boolean>(
      false,
      mergeTyped(submitOnClick$.pipe(mapTo(false)), companyNameIsValid$)
    );

    /* extract values */

    const submitButtonEnabled = useStreamValue(submitButtonEnabled$, false);

    const koumutenId = useStreamValue(koumutenId$);

    const companyName = useStreamValue(companyName$, companyInit.name);
    const companyNameErrors = useStreamValue(companyNameErrors$);

    /* event handler */

    const closeOnClick = useCallback(
      (reason: '' | 'cancel' | 'ok') => {
        if (reason !== 'ok') {
          onClose(reason);
        } else {
          submitOnClick();
          if (
            token !== undefined &&
            koumutenId !== undefined &&
            companyName !== undefined
          ) {
            updateCompany(token)(companyInit.id)({
              koumutenId,
              name: companyName
            }).then(res => {
              setFetchResult(res);
              if (res.type === 'success') {
                onClose(reason);
                fetchCompanyListOf(koumutenId);
              }
            });
          }
        }
      },
      [
        onClose,
        koumutenId,
        companyName,
        companyInit,
        token,
        setFetchResult,
        submitOnClick
      ]
    );

    return (
      <EditCompanyDialogView
        open={open}
        onClose={closeOnClick}
        companyName={companyName}
        companyNameErrors={companyNameErrors}
        companyNameOnInput={companyNameOnInput}
        submitButtonEnabled={submitButtonEnabled}
        nonFieldErrors={nonFieldErrors}
      />
    );
  }
);

EditCompanyDialog.displayName = 'EditCompanyDialog';
